import { Platform, useWindowDimensions } from "@-/com.npmjs.react-native";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { LEARN_CARD_CLEARANCE } from "@-/dev.improve.haniverse.components.ui.learn-card-shell";
import {
  getPrimaryNavigationBarHeight,
  PrimaryNavigationStripFadeInsetContext,
} from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { useContext } from "react";

/** The bar's thickness where the card has no bottom strip to balance (landscape, web, hidden strip). */
const COMPACT_PROGRESS_HEIGHT = 6;
/** Devices without a home indicator would otherwise get a slab rather than a bar. */
const MAX_PROGRESS_HEIGHT = 24;

/**
 * Portrait recharge band and card clearance, shared by the deck and its summary underlay.
 * Landscape moves recharge beside the card, which starts just 9pt below the scene's edge.
 *
 * The bar keeps {@link LEARN_CARD_CLEARANCE} from the header controls and from the card's top
 * border. A portrait native header already reserves the upper gap beneath its buttons; otherwise
 * the stage supplies it.
 *
 * In portrait with the primary navigation strip, the bar fills the band that balances the card:
 * the top safe-area edge sits as far above the card as the bottom safe-area edge sits below it.
 * Below the card are its 9pt gutter, the action row (the header's diameter plus 9pt either side)
 * and the navigation strip minus the bottom inset; above it are the header row (the same diameter
 * plus 9pt either side) and this band. The band is therefore the strip's height above the safe
 * area, and the bar is that band less its 9pt gap to the card.
 */
export function useLearnFlashcardStageTopInsets() {
  const { height, width } = useWindowDimensions();
  const hasTopToolbar = Platform.OS !== "web";
  const insets = useSafeAreaInsets();
  const isStripVisible = useContext(PrimaryNavigationStripFadeInsetContext) > 0;
  const hasBottomStrip = width <= height && isStripVisible;
  const [androidUsesButtonNavBar] = useContextSelectors(
    NavContext,
    (c) => c.androidUsesButtonNavBar,
  );
  const progressTop = hasTopToolbar ? 0 : LEARN_CARD_CLEARANCE;
  const progressHeight =
    hasTopToolbar && hasBottomStrip
      ? Math.min(
          MAX_PROGRESS_HEIGHT,
          Math.max(
            COMPACT_PROGRESS_HEIGHT,
            getPrimaryNavigationBarHeight(insets.bottom, { androidUsesButtonNavBar }) -
              insets.bottom -
              LEARN_CARD_CLEARANCE,
          ),
        )
      : COMPACT_PROGRESS_HEIGHT;
  return {
    cardTop:
      hasTopToolbar && width > height
        ? LEARN_CARD_CLEARANCE
        : progressTop + progressHeight + LEARN_CARD_CLEARANCE,
    progressHeight,
    progressTop,
  };
}
